/**
 * THE SHARED INACTIVITY CLOCK
 * We use `localStorage` to coordinate the inactivity timer across all open tabs.
 * * Why localStorage and not BroadcastChannel?
 * - If we used BroadcastChannel, a tab that is frozen/sleeping in the background
 * would miss the "I am active!" messages flying through the air.
 * - By writing the timestamp to a physical "wall clock" (`localStorage`), a frozen tab
 * can simply look at the clock the exact millisecond it wakes up and know exactly
 * how much time has passed.
 */
const LAST_ACTIVITY_KEY = 'oir_last_activity';

/** The idle time allowed before the "Are you still there?" warning modal appears. */
export const INACTIVITY_LIMIT_MS = 15 * 60 * 1000; // 15 minutes

/** The grace period (countdown) before the user is actually kicked out. */
export const WARNING_COUNTDOWN_MS = 60 * 1000;
export const WARNING_COUNTDOWN_SECONDS = WARNING_COUNTDOWN_MS / 1000;

/**
 * Throttle limit for saving activity.
 * Moving the mouse fires dozens of events per second. We only write to localStorage
 * a maximum of once per second to avoid freezing the browser.
 */
export const ACTIVITY_THROTTLE_MS = 1000;

// Safety check: In strict privacy modes (or sandboxed iframes), just trying
// to touch `localStorage` can crash the app. This prevents the White Screen of Death.
const isLocalStorageAvailable = (): boolean => {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
};

const readTimestamp = (key: string): number | null => {
  if (!isLocalStorageAvailable()) {
    return null;
  }
  const raw = localStorage.getItem(key);
  if (raw === null) {
    return null;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const writeTimestamp = (key: string, timestamp: number): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }
  try {
    localStorage.setItem(key, String(timestamp));
  } catch {
    // If the browser refuses to write (e.g., storage quota exceeded or Private Mode),
    // we silently ignore it. The app won't crash; the inactivity monitor will just
    // degrade safely and track time locally for this specific tab.
  }
};

/** Updates the shared clock. Other tabs will instantly know the user did something. */
export const markActivity = (timestamp: number = Date.now()): void => writeTimestamp(LAST_ACTIVITY_KEY, timestamp);

/** Returns the exact time the user was last active, or `null` if memory is wiped/blocked. */
export const readLastActivity = (): number | null => readTimestamp(LAST_ACTIVITY_KEY);

/** Wipes the clock completely. Called on logout so the next user starts fresh. */
export const clearActivity = (): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    // Silently ignore errors.
  }
};

/**
 * Listens for activity coming from OTHER tabs.
 * The browser's native `storage` event is magic: it ONLY fires in tabs that
 * DID NOT make the change. So, there is no risk of an infinite echo loop.
 * Returns an unsubscribe function (perfect for React useEffect cleanup).
 */

type ActivityHandler = (timestamp: number) => void;
type UnsubscribeFunction = () => void;

export const subscribeToActivity = (handler: ActivityHandler): UnsubscribeFunction => {
  if (typeof globalThis === 'undefined') {
    return () => undefined;
  }

  const listener = (event: StorageEvent): void => {
    if (event.key !== LAST_ACTIVITY_KEY || !event.newValue) {
      return;
    }

    const timestamp = Number(event.newValue);

    if (Number.isFinite(timestamp)) {
      handler(timestamp);
    }
  };

  globalThis.addEventListener('storage', listener);
  return () => globalThis.removeEventListener('storage', listener);
};
